import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import {
  ApiError,
  drawWriterPortrait,
  getEditableWriter,
  updateWriter,
  uploadWriterPortrait,
  type Writer,
} from "../api";
import BackLink from "../components/BackLink";
import CharacterFace from "../components/CharacterFace";
import { useT } from "../i18n";

/**
 * WriterEditPage is a writer's name, dates and bio in the studio
 * (`/studio/writers/:writerID`, requirement 0118 W2). A writer only this
 * library's books name is its creators' to edit; once another library's book
 * names it too — or it is SageLeaf's own — the server answers 403 and only a
 * platform admin may, so the page says that rather than offering a form that
 * cannot save.
 */
export default function WriterEditPage() {
  const { writerID = "" } = useParams();
  const [params] = useSearchParams();
  const bookID = params.get("book") ?? "";
  const t = useT();
  const client = useQueryClient();
  const writer = useQuery({
    queryKey: ["writer-edit", writerID],
    queryFn: () => getEditableWriter(writerID),
    retry: false,
  });
  const [name, setName] = useState("");
  const [dates, setDates] = useState("");
  const [bio, setBio] = useState("");
  useEffect(() => {
    if (!writer.data) return;
    setName(writer.data.name);
    setDates(writer.data.dates ?? "");
    setBio(writer.data.bio ?? "");
  }, [writer.data]);
  const save = useMutation({
    mutationFn: () => updateWriter(writerID, { name, dates, bio }),
    onSuccess: (updated) => {
      client.setQueryData(["writer-edit", writerID], updated);
      // Every book naming this writer shows the name.
      void client.invalidateQueries({ queryKey: ["book"] });
      void client.invalidateQueries({ queryKey: ["writer-options"] });
    },
  });

  // Upload or Draw sets the portrait at once (0118 W3): a writer has one face,
  // and drawing again is how to change it.
  const fileInput = useRef<HTMLInputElement>(null);
  const portraitSet = (updated: Writer) => {
    client.setQueryData(["writer-edit", writerID], updated);
    void client.invalidateQueries({ queryKey: ["writer", writerID] });
  };
  const upload = useMutation({
    mutationFn: (file: File) => uploadWriterPortrait(writerID, file),
    onSuccess: portraitSet,
  });
  const draw = useMutation({
    mutationFn: () => drawWriterPortrait(writerID),
    onSuccess: portraitSet,
  });
  const portraitBusy = upload.isPending || draw.isPending;
  const portraitError = upload.error ?? draw.error;

  const shared = writer.error instanceof ApiError && writer.error.status === 403;
  const field = "w-full rounded-control border border-line bg-surface px-3 py-2 text-sm";

  return (
    <main className="mx-auto max-w-3xl px-4 py-10" data-testid="writer-edit-page">
      {bookID && (
        <nav className="mb-6 text-sm">
          <BackLink to={`/studio/books/${bookID}/settings`}>{t.writer.backToBook}</BackLink>
        </nav>
      )}
      <h1 className="mb-6 text-2xl font-semibold tracking-tight">{t.writer.editTitle}</h1>
      {shared && (
        <p data-testid="writer-shared" className="text-sm text-muted">
          {t.writer.shared}
        </p>
      )}
      {writer.isError && !shared && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {t.writer.notFound}
        </p>
      )}
      {writer.data && (
        <section className="mb-8" data-testid="writer-portrait-section">
          <h2 className="mb-2 text-sm font-medium">{t.writer.portrait}</h2>
          <div className="flex items-center gap-4">
            <CharacterFace character={writer.data} size={96} />
            <div className="space-y-2">
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="writer-portrait-upload"
                  disabled={portraitBusy}
                  onClick={() => fileInput.current?.click()}
                  className="rounded-control border border-line px-3 py-1.5 text-sm font-medium transition-colors hover:bg-sunk disabled:opacity-40"
                >
                  {t.writer.uploadPortrait}
                </button>
                <button
                  type="button"
                  data-testid="writer-portrait-draw"
                  disabled={portraitBusy}
                  onClick={() => draw.mutate()}
                  className="rounded-control bg-sage px-3 py-1.5 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
                >
                  {t.writer.drawPortrait}
                </button>
              </div>
              <p className="text-xs text-muted">{t.writer.drawNote}</p>
            </div>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              hidden
              data-testid="writer-portrait-file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) upload.mutate(file);
              }}
            />
          </div>
          {portraitError && (
            <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">
              {portraitError instanceof Error && portraitError.message
                ? portraitError.message
                : t.writer.portraitFailed}
            </p>
          )}
        </section>
      )}
      {writer.data && (
        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <label className="block">
            <span className="mb-1 block text-sm font-medium">{t.writer.name}</span>
            <input
              dir="auto"
              data-testid="writer-name-input"
              required
              maxLength={300}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={field}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">{t.writer.dates}</span>
            <input
              dir="auto"
              data-testid="writer-dates-input"
              maxLength={60}
              value={dates}
              placeholder={t.writer.datesHint}
              onChange={(e) => setDates(e.target.value)}
              className={field}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">{t.writer.bio}</span>
            <textarea
              dir="auto"
              data-testid="writer-bio-input"
              rows={5}
              maxLength={2000}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              className={field}
            />
          </label>
          <div className="flex items-center gap-3">
            <button
              type="submit"
              data-testid="writer-save"
              disabled={save.isPending || !name.trim()}
              className="rounded-control bg-sage px-4 py-2 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
            >
              {t.writer.save}
            </button>
            {save.isSuccess && !save.isPending && (
              <span data-testid="writer-saved" className="text-sm text-muted">
                {t.writer.saved}
              </span>
            )}
          </div>
          {save.isError && (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
              {save.error instanceof Error ? save.error.message : t.writer.editFailed}
            </p>
          )}
        </form>
      )}
    </main>
  );
}
